import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { accessToken, signOut as authSignOut } from './auth';
import { api, ApiError, setUnauthorisedHandler } from './api';

export type Role = 'teacher' | 'school_leader' | 'hybrid' | 'system_admin';

export interface Me {
  id: string;
  email: string;
  name: string;
  role: Role;
  is_owner: number | boolean;
  job_title: string | null;
  school_id: string | null;
  /** When the school finished web setup; null until then. */
  school_onboarded_at: string | null;
  /** False when the school's setup still lacks required answers (older schools); absent on older APIs. */
  school_setup_complete?: boolean;
  /** organisation, or personal (one person's own account); absent on older APIs. */
  school_type?: 'organisation' | 'personal';
  /** An EducAitor admin has paused the organisation; absent on older APIs. */
  school_paused?: number | boolean;
  /** 'removed' after a leader or admin takes them out of their organisation. */
  /** 'none': an account with no organisation, paused until it joins one. */
  join_status?: 'active' | 'pending' | 'declined' | 'removed' | 'none';
  /** With join_status 'removed': the organisation they were removed from. */
  pending_school_name?: string | null;
  /** Owners' names, sent to a leader who isn't an owner while setup is unfinished. */
  school_owners?: string[];
  school_name: string | null;
  school_invite_code: string | null;
  avatar_url: string | null;
}

type State =
  | { status: 'loading' }
  | { status: 'signedOut' }
  /** Signed in to Cognito but no EducAitor profile yet (sign-up not finished). */
  | { status: 'unregistered' }
  | { status: 'signedIn'; me: Me };

interface Session {
  state: State;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function isLeader(role: Role) {
  return role === 'school_leader' || role === 'hybrid' || role === 'system_admin';
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  // Everything loaded belongs to whoever is signed in: forget it when they
  // sign out, or when someone else signs in on the same tab, so nobody sees
  // the last person's data (their organisation, Hub, people) even briefly.
  const qc = useQueryClient();
  const lastUser = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    if (!(await accessToken())) {
      setState({ status: 'signedOut' });
      return;
    }
    try {
      const me = await api<Me>('/auth/sync', { method: 'POST', body: '{}' });
      if (lastUser.current && lastUser.current !== me.id) qc.clear();
      lastUser.current = me.id;
      setState({ status: 'signedIn', me });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setState({ status: 'unregistered' });
      else if (err instanceof ApiError && err.status === 401) setState({ status: 'signedOut' });
      else throw err;
    }
  }, [qc]);

  const signOut = useCallback(async () => {
    await authSignOut();
    qc.clear();
    lastUser.current = null;
    setState({ status: 'signedOut' });
  }, [qc]);

  useEffect(() => {
    setUnauthorisedHandler(() => {
      authSignOut().finally(() => {
        qc.clear();
        lastUser.current = null;
        setState({ status: 'signedOut' });
      });
    });
    refresh().catch(() => setState({ status: 'signedOut' }));
  }, [refresh, qc]);

  const value = useMemo(() => ({ state, refresh, signOut }), [state, refresh, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/** The signed-in profile; only use under a route that requires sign-in. */
export function useMe(): Me {
  const { state } = useSession();
  if (state.status !== 'signedIn') throw new Error('useMe without a signed-in user');
  return state.me;
}
